import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

const routes: RouteRecordRaw[] = [
	{
		path: '/',
		name: 'Portal',
		component: () => import('@/views/PortalPage.vue'),
	},
	{
		path: '/workspace',
		name: 'Workspace',
		component: () => import('@/views/WorkspacePage.vue'),
	},
	{
		path: '/loading',
		name: 'Loading',
		component: () => import('@/views/LoadingPage.vue'),
		meta: { requiresAuth: true },
	},
	{
		path: '/analyzing/:taskId',
		name: 'Analyzing',
		component: () => import('@/views/AnalyzingPage.vue'),
		meta: { requiresAuth: true },
	},
	{
		path: '/result/:taskId',
		name: 'Result',
		component: () => import('@/views/ResultPage.vue'),
		meta: { requiresAuth: true },
	},
	{
		path: '/taotu/:taskId',
		name: 'Taotu',
		component: () => import('@/views/TaotuPage.vue'),
		meta: { requiresAuth: true },
	},
	{
		path: '/demo/:section',
		name: 'Demo',
		component: () => import('@/views/DemoPage.vue'),
		meta: { title: '效果示例' },
	},
	{
		path: '/history',
		name: 'History',
		component: () => import('@/views/HistoryPage.vue'),
		meta: { requiresAuth: true },
	},
	{
		path: '/recharge',
		name: 'Recharge',
		component: () => import('@/views/RechargePage.vue'),
		meta: { requiresAuth: true },
	},
	{
		path: '/credits',
		name: 'Credits',
		component: () => import('@/views/CreditLogPage.vue'),
		meta: { requiresAuth: true },
	},
	{
		path: '/auth',
		name: 'Auth',
		component: () => import('@/views/AuthPage.vue'),
	},
	{
		path: '/admin',
		component: () => import('@/components/AdminLayout.vue'),
		meta: { requiresAuth: true, requiresAdmin: true },
		children: [
			{
				path: '',
				name: 'Admin',
				component: () => import('@/views/AdminPage.vue'),
			},
			{
				path: 'template',
				name: 'AdminTemplate',
				component: () => import('@/views/AdminTemplatePage.vue'),
			},
			{
				path: 'log',
				name: 'AdminLog',
				component: () => import('@/views/AdminLogPage.vue'),
			},
			{
				path: 'tt_preview',
				name: 'AdminTtPreview',
				component: () => import('@/views/AdminTtPreviewPage.vue'),
			},
			{
				path: 'skill-learning',
				name: 'AdminSkillLearning',
				component: () => import('@/views/AdminSkillLearningPage.vue'),
			},
			{
				path: 'skill-learning/:taskId',
				name: 'AdminSkillLearningDetail',
				component: () => import('@/views/AdminSkillLearningDetailPage.vue'),
			},
			{
				path: 'skill',
				name: 'AdminSkill',
				component: () => import('@/views/AdminSkillPage.vue'),
			},
				{
					path: 'skill-stats',
					name: 'AdminSkillStats',
					component: () => import('@/views/AdminSkillStatsPage.vue'),
				},
			{
				path: 'model-stats',
				name: 'AdminModelStats',
				component: () => import('@/views/AdminModelStatsPage.vue'),
			},
			{
				path: 'credits',
				name: 'AdminCredits',
				component: () => import('@/views/AdminCreditsPage.vue'),
			},
			{
				path: 'tasks',
				name: 'AdminTasks',
				component: () => import('@/views/AdminTaskListPage.vue'),
			},
		],
	},
	{
		path: '/403',
		name: 'Error403',
		component: () => import('@/views/ErrorPage.vue'),
		meta: { errorCode: '403' },
	},
	{
		path: '/404',
		name: 'Error404',
		component: () => import('@/views/ErrorPage.vue'),
		meta: { errorCode: '404' },
	},
	{
		path: '/:pathMatch(.*)*',
		name: 'NotFound',
		component: () => import('@/views/ErrorPage.vue'),
		meta: { errorCode: '404' },
	},
]

const router = createRouter({
	history: createWebHistory(),
	routes,
})

router.beforeEach(async (to, _from, next) => {
	const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
	const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin)
	const token = localStorage.getItem('token')

	if (requiresAuth && !token) {
		next({ path: '/auth', query: { redirect: to.fullPath } })
		return
	}

	if (to.path === '/auth' && token) {
		next({ path: '/workspace' })
		return
	}

	// Dynamic import: avoids circular dependency router ↔ auth store
	if (token && (requiresAuth || requiresAdmin)) {
		try {
			const { useAuthStore } = await import('@/stores/auth')
			const authStore = useAuthStore()
			await authStore.fetchUser()

			if (requiresAdmin && !authStore.user?.is_admin) {
				next({ path: '/403' })
				return
			}
		} catch {
			localStorage.removeItem('token')
			next({ path: '/auth', query: { redirect: to.fullPath } })
			return
		}
	}

	if (token && !requiresAuth) {
		// Dynamic import: avoids circular dependency router ↔ auth store
		// Non-auth pages: fetch user in background (non-blocking)
		import('@/stores/auth').then(({ useAuthStore }) => {
			useAuthStore().fetchUser().catch(() => {})
		})
	}

	next()
})

export default router
